import { notify } from '@/components/ui/ConfirmHost';
import { askConfirm } from '@/components/ui/ConfirmHost';
import React, { useState, useRef } from 'react';
import { useBase } from '@/lib/useBase';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  getStudents, postStudent, putStudent, withdrawStudent, reinstateStudent,
  getClasses, getDashboard, importStudents, getGuardians,
  postGuardian, linkGuardianToStudent, uploadStudentDocument,
} from '@/api/owner';
import {
  Plus, Upload, Edit2, UserMinus, UserCheck, X, Users, Link2,
  Eye, ImagePlus,
} from 'lucide-react';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};

const STATUS_TONE: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  withdrawn: 'bg-red-100 text-red-700',
  alumni: 'bg-blue-100 text-blue-700',
  graduated: 'bg-violet-100 text-violet-700',
};

const RELATIONSHIPS = ['father', 'mother', 'guardian', 'other'];

function passportUrl(student: any): string | null {
  const docs = arr<any>(student?.documents);
  if (!docs.length) return null;
  // Prefer explicit passport type
  const byType = docs.find((d: any) => String(d.type ?? '').toLowerCase().includes('passport'));
  if (byType?.url ?? byType?.file_url) return byType.url ?? byType.file_url;
  // Fall back to first image-extension document
  const isImage = (url?: string | null) => !!url && /\.(png|jpe?g|webp|gif)$/i.test(url);
  const image = docs.find((d: any) => isImage(d.url) || isImage(d.file_url));
  return image?.url ?? image?.file_url ?? null;
}

export function StudentsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const base = useBase();
  const [filter, setFilter] = useState<'all' | 'alumni' | 'withdrawn' | number>('all');
  const [params] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  React.useEffect(() => { const q = params.get('q'); if (q !== null) setSearch(q); }, [params]);
  const [modal, setModal] = useState<
    | null
    | { kind: 'add' }
    | { kind: 'edit'; student: any }
    | { kind: 'withdraw'; student: any }
    | { kind: 'link'; student: any }
    | { kind: 'reinstate'; student: any }
  >(null);

  const dashQ = useQuery({ queryKey: ['owner-dashboard'], queryFn: () => getDashboard().then((r: any) => r.data?.data ?? r.data), staleTime: 60_000 });
  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses().then((r: any) => r.data?.data ?? r.data) });
  const studentsQ = useQuery({ queryKey: ['owner-students'], queryFn: () => getStudents({ per_page: 3000, status: 'all' }).then((r: any) => r.data?.data ?? r.data) });
  const guardiansQ = useQuery({ queryKey: ['owner-guardians'], queryFn: () => getGuardians().then((r: any) => r.data?.data ?? r.data) });

  const dash = dashQ.data as any;
  const classes = arr<any>(classesQ.data);
  const students = arr<any>(studentsQ.data);
  const guardians = arr<any>(guardiansQ.data);

  const label = [dash?.school?.name, dash?.session?.name, dash?.term?.name].filter(Boolean).join(' · ');

  const filtered = students.filter((s: any) => {
    const q = search.toLowerCase();
    if (q && !`${s.first_name ?? ''} ${s.last_name ?? ''} ${s.admission_no ?? ''} ${s.guardian_name ?? ''}`.toLowerCase().includes(q)) return false;
    if (filter === 'all') return s.status === 'active';
    if (filter === 'alumni' || filter === 'withdrawn') return s.status === filter;
    return Number(s.current_class_id) === Number(filter) && s.status === 'active';
  });

  const countForClass = (cid: number) => students.filter((s: any) => Number(s.current_class_id) === cid && s.status === 'active').length;
  const alumniCount = students.filter((s: any) => s.status === 'alumni').length;
  const withdrawnCount = students.filter((s: any) => s.status === 'withdrawn').length;
  const activeCount = students.filter((s: any) => s.status === 'active').length;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['owner-students'] });
    qc.invalidateQueries({ queryKey: ['owner-dashboard'] });
    qc.invalidateQueries({ queryKey: ['owner-guardians'] });
    setModal(null);
  };

  // Full save flow: student (+ guardian bundled) → photo upload
  let lastSaveData: any = null;
  const saveM = useMutation({
    mutationFn: async (data: {
      student: any;
      isEdit: boolean;
      studentId?: number;
      photo?: File | null;
      parent?: {
        first_name: string;
        last_name: string;
        phone: string;
        email?: string;
        relationship: string;
        password?: string;
      } | null;
      confirmDuplicate?: boolean;
    }) => {
      lastSaveData = data;
      let studentId = data.studentId ?? 0;

      // 1. Create or update student
      if (data.isEdit && studentId) {
        await putStudent(studentId, data.student);
      } else {
        const payload: any = { ...data.student };
        // Backend accepts guardian object directly — atomic create + link
        if (data.parent && data.parent.first_name && data.parent.phone) {
          payload.guardian = {
            first_name: data.parent.first_name,
            last_name: data.parent.last_name || '',
            phone: data.parent.phone,
            email: data.parent.email || undefined,
            relationship: data.parent.relationship || 'guardian',
            ...(data.parent.password ? { password: data.parent.password } : {}),
          };
        }
        if (data.confirmDuplicate) {
          payload.confirm_duplicate = true;
        }
        const res = await postStudent(payload);
        const created = res.data?.data ?? res.data;
        studentId = created?.id ?? created?.student?.id;
        if (!studentId) throw new Error('Student was created but no ID returned');
        const adm = created?.admission_no ?? created?.student?.admission_no;
        if (adm && (created?.temp_password || payload.password)) {
          void notify(`${created.first_name ?? ''} ${created.last_name ?? ''} can sign in now.\n\nAdmission number: ${adm}\nPassword: ${payload.password ? 'the one you set' : created.temp_password}`, { title: 'Student login ready', tone: 'success' });
        }
      }

      // 2. Upload passport photo if provided
      if (data.photo && studentId) {
        try {
          const fd = new FormData();
          fd.append('type', 'passport_photo');
          fd.append('file', data.photo);
          await uploadStudentDocument(studentId, fd);
        } catch (e: any) {
          console.error('Photo upload failed:', e);
        }
      }

      return studentId;
    },
    onSuccess: refresh,
    onError: (e: any) => {
      // Handle 409 duplicate gracefully
      const status = e.response?.status;
      const message = e.response?.data?.message ?? e.message ?? 'Failed';
      if (status === 409 && /same name and date of birth/i.test(message)) {
        void askConfirm('A student with the same name and date of birth already exists. Add this one anyway?', { title: 'Possible duplicate', confirmLabel: 'Add anyway' }).then((force) => {
          // Re-submit with confirm_duplicate
          if (force) saveM.mutate({ ...lastSaveData, confirmDuplicate: true });
        });
      } else {
        alert(message);
      }
    },
  });

  const withdrawM = useMutation({
    mutationFn: (d: { id: number; body: any }) => withdrawStudent(d.id, d.body),
    onSuccess: refresh,
    onError: (e: any) => alert(e.response?.data?.message ?? 'Failed'),
  });
  const reinstateM = useMutation({
    mutationFn: (d: { id: number; body: { school_class_id: number; password?: string } }) => reinstateStudent(d.id, d.body),
    onSuccess: (r: any) => {
      const login = r.data?.data?.login;
      setModal(null);
      refresh();
      if (login) void notify(`They can sign in now.\nAdmission number: ${login.admission_no}\nPassword: ${login.password}`, { title: 'Student is active again', tone: 'success' });
      else void notify('The student is active again.', { tone: 'success' });
    },
    onError: (e: any) => alert(e.response?.data?.message ?? 'Failed'),
  });
  const linkM = useMutation({
    mutationFn: (d: { guardianId: number; body: any }) => linkGuardianToStudent(d.guardianId, d.body),
    onSuccess: refresh,
    onError: (e: any) => alert(e.response?.data?.message ?? 'Failed'),
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const importM = useMutation({
    mutationFn: (fd: FormData) => importStudents(fd),
    onSuccess: (r: any) => { alert(r.data?.message ?? 'Import complete'); refresh(); },
    onError: (e: any) => alert(e.response?.data?.message ?? 'Import failed'),
  });
  const onImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    importM.mutate(fd);
    e.target.value = '';
  };

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto pb-10 p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{label || 'School · Loading…'}</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Students</h1>
          <p className="text-slate-500 text-sm mt-1">{activeCount} enrolled · {classes.length} class{classes.length === 1 ? '' : 'es'} · {alumniCount} alumni · {withdrawnCount} withdrawn</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="file" ref={fileInputRef} className="hidden" accept=".csv,.xlsx,.xls" onChange={onImportFile} />
          <button onClick={() => fileInputRef.current?.click()} disabled={importM.isPending} className="bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-lg font-semibold text-sm shadow-sm hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50">
            <Upload className="w-4 h-4" /> {importM.isPending ? 'Importing…' : 'Import'}
          </button>
          <button onClick={() => setModal({ kind: 'add' })} className="bg-[#0f172a] hover:bg-slate-800 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-sm flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add student
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter('all')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filter === 'all' ? 'bg-[#0f172a] text-white' : 'bg-white border border-slate-300 text-slate-700'}`}>
          All ({activeCount})
        </button>
        {classes.map((c: any) => (
          <button key={c.id} onClick={() => setFilter(c.id)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filter === c.id ? 'bg-[#0f172a] text-white' : 'bg-white border border-slate-300 text-slate-700'}`}>
            {c.name}{c.arm ? ` ${c.arm}` : ''} ({countForClass(c.id)})
          </button>
        ))}
        <button onClick={() => setFilter('alumni')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filter === 'alumni' ? 'bg-[#0f172a] text-white' : 'bg-white border border-slate-300 text-slate-700'}`}>
          Alumni ({alumniCount})
        </button>
        <button onClick={() => setFilter('withdrawn')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filter === 'withdrawn' ? 'bg-red-600 text-white' : 'bg-white border border-red-200 text-red-700'}`}>
          Withdrawn ({withdrawnCount})
        </button>
      </div>

      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, reg no. or parent" className="w-full border border-slate-300 rounded-lg px-4 py-2.5 text-sm bg-white" />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {studentsQ.isLoading ? <p className="p-8 text-center text-sm text-slate-500">Loading…</p> :
         filtered.length === 0 ? (
           <div className="p-16 text-center">
             <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
             <p className="font-bold text-slate-700 mb-1">No students {filter === 'all' ? 'yet' : 'in this view'}</p>
             <p className="text-sm text-slate-500">Add your first student or import a CSV</p>
           </div>
         ) : (
           <table className="w-full text-left text-sm">
             <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
               <tr>
                 <th className="px-4 py-3">Name</th>
                 <th className="px-4 py-3">Class</th>
                 <th className="px-4 py-3">Reg. No.</th>
                 <th className="px-4 py-3">Parent</th>
                 <th className="px-4 py-3">Status</th>
                 <th className="px-4 py-3 text-right">Actions</th>
               </tr>
             </thead>
             <tbody className="divide-y divide-slate-100">
               {filtered.map((s: any) => {
                 const photo = passportUrl(s);
                 return (
                   <tr
                     key={s.id}
                     onClick={() => navigate(`${base}/students/${s.id}`)}
                     className="hover:bg-slate-50 cursor-pointer"
                   >
                     <td className="px-4 py-2">
                       <div className="flex items-center gap-2">
                         {photo ? (
                           <img src={photo} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                         ) : (
                           <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600 flex-shrink-0">
                             {(s.first_name?.[0] ?? '') + (s.last_name?.[0] ?? '')}
                           </div>
                         )}
                         <span className="font-semibold text-slate-900">{s.first_name} {s.last_name}</span>
                       </div>
                     </td>
                     <td className="px-4 py-2 text-slate-600 text-xs">{s.class ?? s.current_class?.name ?? '—'}</td>
                     <td className="px-4 py-2 font-mono text-xs">{s.admission_no ?? '—'}</td>
                     <td className="px-4 py-2 text-slate-600 text-xs">{s.guardian_name ?? '—'}</td>
                     <td className="px-4 py-2">
                       <span className={`text-xs font-bold px-2 py-0.5 rounded ${STATUS_TONE[s.status] ?? 'bg-slate-100 text-slate-600'}`}>
                         {s.status ?? 'active'}
                       </span>
                     </td>
                     <td className="px-4 py-2 text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                       <Link to={`${base}/students/${s.id}`} title="View profile" className="text-xs font-semibold text-slate-600 hover:underline p-1 inline-block">
                         <Eye className="w-3.5 h-3.5" />
                       </Link>
                       <button onClick={() => setModal({ kind: 'link', student: s })} title="Link parent" className="text-xs font-semibold text-blue-600 hover:underline p-1">
                         <Link2 className="w-3.5 h-3.5 inline" />
                       </button>
                       <button onClick={() => setModal({ kind: 'edit', student: s })} title="Edit" className="text-xs font-semibold text-brand-600 hover:underline p-1">
                         <Edit2 className="w-3.5 h-3.5 inline" />
                       </button>
                       {s.status === 'active' ? (
                         <button onClick={() => setModal({ kind: 'withdraw', student: s })} title="Withdraw" className="text-xs font-semibold text-red-600 hover:underline p-1">
                           <UserMinus className="w-3.5 h-3.5 inline" />
                         </button>
                       ) : s.status === 'withdrawn' ? (
                         <button onClick={() => setModal({ kind: 'reinstate', student: s })} title="Make active again" className="text-xs font-semibold text-green-600 hover:underline p-1">
                           <UserCheck className="w-3.5 h-3.5 inline" />
                         </button>
                       ) : null}
                     </td>
                   </tr>
                 );
               })}
             </tbody>
           </table>
         )}
      </div>

      {(modal?.kind === 'add' || modal?.kind === 'edit') && (
        <StudentModal
          student={modal.kind === 'edit' ? modal.student : null}
          classes={classes}
          onClose={() => setModal(null)}
          onSave={(data: any) => saveM.mutate(data)}
          saving={saveM.isPending}
        />
      )}

      {modal?.kind === 'reinstate' && (
        <ReinstateModal student={modal.student} classes={classes} onClose={() => setModal(null)} saving={reinstateM.isPending}
          onSave={(body: { school_class_id: number; password?: string }) => reinstateM.mutate({ id: modal.student.id, body })} />
      )}
      {modal?.kind === 'withdraw' && (
        <WithdrawModal student={modal.student} onClose={() => setModal(null)} onSave={(reason: string) => withdrawM.mutate({ id: modal.student.id, body: { reason } })} saving={withdrawM.isPending} />
      )}

      {modal?.kind === 'link' && (
        <LinkParentModal
          student={modal.student}
          guardians={guardians}
          onClose={() => setModal(null)}
          onSave={(guardianId: number, body: any) => linkM.mutate({ guardianId, body })}
          saving={linkM.isPending}
        />
      )}
    </div>
  );
}

function StudentModal({ student, classes, onClose, onSave, saving }: any) {
  const isEdit = !!student;
  const [first, setFirst] = useState(student?.first_name ?? '');
  const [middle, setMiddle] = useState(student?.middle_name ?? '');
  const [last, setLast] = useState(student?.last_name ?? '');
  const [gender, setGender] = useState(student?.gender ?? 'male');
  const [dob, setDob] = useState(student?.date_of_birth ? student.date_of_birth.slice(0, 10) : '');
  const [classId, setClassId] = useState<number | ''>(student?.current_class_id ?? '');
  const [bloodGroup, setBloodGroup] = useState(student?.blood_group ?? '');
  const [medical, setMedical] = useState(student?.medical_notes ?? '');
  const [studentPw, setStudentPw] = useState('');
  const [showStudentPw, setShowStudentPw] = useState(false);

  // Passport photo state
  const existingPhoto = passportUrl(student);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(existingPhoto);
  const fileRef = useRef<HTMLInputElement>(null);

  const onPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(existingPhoto);
    if (fileRef.current) fileRef.current.value = '';
  };

  // Parent fields (only on add)
  const [parentFirst, setParentFirst] = useState('');
  const [parentLast, setParentLast] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentPassword, setParentPassword] = useState('');
  const [showParentPw, setShowParentPw] = useState(false);
  const [parentRelationship, setParentRelationship] = useState('guardian');
  const [addParent, setAddParent] = useState(true);

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="font-bold text-slate-900">{isEdit ? 'Edit student' : 'Add student'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => {
          e.preventDefault();
          const studentData: any = {
            first_name: first, middle_name: middle || undefined, last_name: last,
            gender, date_of_birth: dob || undefined,
            current_class_id: classId ? Number(classId) : undefined,
            blood_group: bloodGroup || undefined, medical_notes: medical || undefined,
            ...(!isEdit && studentPw ? { password: studentPw } : {}),
          };
          const parent = (!isEdit && addParent) ? {
            first_name: parentFirst, last_name: parentLast,
            phone: parentPhone, email: parentEmail || undefined,
            relationship: parentRelationship, password: parentPassword || undefined,
          } : null;
          onSave({ student: studentData, isEdit, studentId: student?.id, photo: photoFile, parent });
        }} className="p-6 overflow-y-auto space-y-5">

          {/* PHOTO UPLOAD */}
          <div>
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Passport photo (optional)</div>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0 border border-slate-200">
                {photoPreview ? (
                  <img src={photoPreview} alt="Passport" className="w-full h-full object-cover" />
                ) : (
                  <ImagePlus className="w-8 h-8 text-slate-400" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input type="file" ref={fileRef} className="hidden" accept="image/jpeg,image/png,image/webp" onChange={onPhoto} />
                <button type="button" onClick={() => fileRef.current?.click()} className="text-xs font-bold bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg">
                  {photoPreview ? 'Change photo' : 'Choose photo'}
                </button>
                {photoPreview && photoPreview !== existingPhoto && (
                  <button type="button" onClick={clearPhoto} className="text-xs font-semibold text-red-600 hover:underline">
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* STUDENT SECTION */}
          <div className="border-t border-slate-200 pt-4">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Student</div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div><label className="block text-sm font-semibold mb-1">First name</label><input value={first} onChange={(e) => setFirst(e.target.value)} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
              <div><label className="block text-sm font-semibold mb-1">Middle</label><input value={middle} onChange={(e) => setMiddle(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
              <div><label className="block text-sm font-semibold mb-1">Surname</label><input value={last} onChange={(e) => setLast(e.target.value)} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><label className="block text-sm font-semibold mb-1">Gender</label>
                <select value={gender} onChange={(e) => setGender(e.target.value)} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
                  <option value="male">Male</option><option value="female">Female</option>
                </select>
              </div>
              <div><label className="block text-sm font-semibold mb-1">Date of birth</label><input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            </div>
            {!isEdit && (
              <div className="mb-3">
                <label className="block text-sm font-semibold mb-1">Class</label>
                <select value={classId} onChange={(e) => setClassId(e.target.value ? Number(e.target.value) : '')} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
                  <option value="">Choose class…</option>
                  {classes.map((c: any) => <option key={c.id} value={c.id}>{c.name}{c.arm ? ` ${c.arm}` : ''}</option>)}
                </select>
                {classes.length === 0 && <p className="text-xs text-red-600 mt-1">No classes yet — add one from Classes & Arms first</p>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-semibold mb-1">Blood group</label><input value={bloodGroup} onChange={(e) => setBloodGroup(e.target.value)} placeholder="O+" className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
              <div><label className="block text-sm font-semibold mb-1">Medical notes</label><input value={medical} onChange={(e) => setMedical(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
            </div>
            {!isEdit && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                <label className="block text-sm font-semibold mb-1">Student login password (optional)</label>
                <div className="relative">
                  <input type={showStudentPw ? 'text' : 'password'} value={studentPw} onChange={(e) => setStudentPw(e.target.value)} minLength={6} autoComplete="new-password" placeholder="At least 6 characters" className="w-full border border-slate-300 rounded-lg p-2.5 pr-16 text-sm bg-white" />
                  <button type="button" onClick={() => setShowStudentPw(!showStudentPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500 px-2">{showStudentPw ? 'Hide' : 'Show'}</button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">The student signs in with their admission number (made automatically with the school code) and this password. Leave it empty and a temporary password is shown after saving.</p>
              </div>
            )}
          </div>

          {/* PARENT SECTION (only when adding) */}
          {!isEdit && (
            <div className="border-t border-slate-200 pt-4">
              <div className="flex items-center gap-2 mb-2">
                <input type="checkbox" id="addParent" checked={addParent} onChange={(e) => setAddParent(e.target.checked)} className="w-4 h-4" />
                <label htmlFor="addParent" className="text-xs font-bold text-slate-500 uppercase tracking-wider cursor-pointer">Link parent (auto-created as primary payer)</label>
              </div>
              {addParent && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-sm font-semibold mb-1">Parent first name</label><input value={parentFirst} onChange={(e) => setParentFirst(e.target.value)} required={addParent} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
                    <div><label className="block text-sm font-semibold mb-1">Parent surname</label><input value={parentLast} onChange={(e) => setParentLast(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-sm font-semibold mb-1">Phone</label><input value={parentPhone} onChange={(e) => setParentPhone(e.target.value)} placeholder="08030000000" required={addParent} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
                    <div><label className="block text-sm font-semibold mb-1">Email (optional)</label><input type="email" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-sm font-semibold mb-1">Relationship</label>
                      <select value={parentRelationship} onChange={(e) => setParentRelationship(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
                        {RELATIONSHIPS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                      </select>
                    </div>
                    <div><label className="block text-sm font-semibold mb-1">Parent login password (optional)</label>
                      <div className="relative">
                        <input type={showParentPw ? 'text' : 'password'} value={parentPassword} onChange={(e) => setParentPassword(e.target.value)} minLength={6} autoComplete="new-password" placeholder="At least 6 characters" className="w-full border border-slate-300 rounded-lg p-2.5 pr-14 text-sm" />
                        <button type="button" onClick={() => setShowParentPw(!showParentPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500 px-1">{showParentPw ? 'Hide' : 'Show'}</button>
                      </div>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500">With a password the parent signs in straight away with their phone{parentEmail ? ' or email' : ''}. If this phone already belongs to a parent, their password is changed to this one.</p>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-2 border-t border-slate-200">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">{saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create student'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Make a withdrawn student active again, in the class they are joining. Students brought from the old portal get a login. */
export function ReinstateModal({ student, classes, onClose, onSave, saving }: any) {
  const [classId, setClassId] = useState<string>(String(student.current_class_id ?? ''));
  const [password, setPassword] = useState('');
  const noLogin = !student.user_id && student.has_login !== true;
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="font-bold text-slate-900">Make {student.first_name} {student.last_name} active again</h3>
          <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave({ school_class_id: Number(classId), ...(password ? { password } : {}) }); }} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Class they are joining</label>
            <select value={classId} onChange={(e) => setClassId(e.target.value)} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
              <option value="">Choose a class…</option>
              {(classes ?? []).map((c: any) => <option key={c.id} value={c.id}>{c.name}{c.arm ? ` ${c.arm}` : ''}</option>)}
            </select>
            <p className="text-xs text-slate-500 mt-1">Their past results stay as they are.</p>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Login password {noLogin ? '(optional)' : '(only if they have no login yet)'}</label>
            <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} placeholder="Leave empty for a temporary one" className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" />
            <p className="text-xs text-slate-500 mt-1">They sign in with their reg number {student.admission_no}.</p>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={!classId || saving || (password !== '' && password.length < 6)} className="flex-1 py-2.5 bg-green-600 text-white rounded-lg font-semibold text-sm disabled:opacity-50">{saving ? 'Saving…' : 'Make active'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function WithdrawModal({ student, onClose, onSave, saving }: any) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="font-bold text-slate-900">Withdraw {student.first_name} {student.last_name}?</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(reason); }} className="p-6 space-y-4">
          <p className="text-sm text-slate-600">This deactivates the account and stops billing. The student can be reinstated later.</p>
          <div><label className="block text-sm font-semibold mb-1">Reason (required)</label><textarea value={reason} onChange={(e) => setReason(e.target.value)} required rows={3} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={!reason || saving} className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-semibold text-sm disabled:opacity-50">{saving ? 'Withdrawing…' : 'Withdraw'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LinkParentModal({ student, guardians, onClose, onSave, saving }: any) {
  const [guardianId, setGuardianId] = useState<number | ''>('');
  const [relationship, setRelationship] = useState('guardian');
  const [isPrimary, setIsPrimary] = useState(false);
  const [canPay, setCanPay] = useState(true);

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <div>
            <h3 className="font-bold text-slate-900">Link parent</h3>
            <p className="text-xs text-slate-500 mt-0.5">to {student.first_name} {student.last_name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => {
          e.preventDefault();
          if (!guardianId) return;
          onSave(Number(guardianId), {
            student_id: student.id, relationship, is_primary: isPrimary, can_pay: canPay,
          });
        }} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Existing guardian</label>
            <select value={guardianId} onChange={(e) => setGuardianId(e.target.value ? Number(e.target.value) : '')} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
              <option value="">Choose guardian…</option>
              {guardians.map((g: any) => (
                <option key={g.id} value={g.id}>{g.first_name} {g.last_name} · {g.phone}</option>
              ))}
            </select>
            {guardians.length === 0 && <p className="text-xs text-amber-700 mt-1">No guardians yet — create one on the Families page first</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Relationship</label>
            <select value={relationship} onChange={(e) => setRelationship(e.target.value)} required className="w-full border border-slate-300 rounded-lg p-2.5 text-sm">
              {RELATIONSHIPS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} className="w-4 h-4" />
              <span className="font-semibold text-slate-800">Make primary payer</span>
            </label>
            <p className="text-xs text-slate-600">Only one guardian per student can be primary.</p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={canPay} onChange={(e) => setCanPay(e.target.checked)} className="w-4 h-4" />
            <span className="text-slate-700">Guardian can pay fees</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={saving || !guardianId} className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">{saving ? 'Linking…' : 'Link parent'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
